import { useEffect, useState, useRef } from "react";
import {
  Box,
  Drawer,
  Button,
  IconButton,
  Typography,
  Checkbox,
  useTheme,
  Tabs,
  Tab,
  Chip,
  alpha,
  CircularProgress,
} from "@mui/material";
import { Close, FilterAltOutlined, Check, Clear } from "@mui/icons-material";
import { font } from "../../styles/font";
import { useApplicationContext } from "../../context/ApplicationContext";
import { getProjectSubcategoryList_API } from "../../api/controller/ProjectController";
import { HttpStatusCode } from "axios";
import useErrorHandler from "../../api/errorHandler/useErrorHandler";

export default function ProjectFilterDrawer({ open, setOpen }) {
  const theme = useTheme();
  const errorHandler = useErrorHandler();

  const {
    categoryList,
    activeProjectCategory,
    setActiveProjectCategory,
    activeProjectSubcategory,
    setActiveProjectSubcategory,
  } = useApplicationContext();

  // Use refs to prevent unnecessary re-renders
  const isInitialMount = useRef(true);
  const lastSelectedCategoryRef = useRef(null);

  // Local state for drawer - doesn't depend on parent re-renders
  const [filterData, setFilterData] = useState({
    category: null,
    subcategory: null,
  });

  const [activeTab, setActiveTab] = useState("category");
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [localSubcategories, setLocalSubcategories] = useState([]);
  const [isTabSwitchInProgress, setIsTabSwitchInProgress] = useState(false);

  // Initialize filter data from context only on first open
  useEffect(() => {
    if (open) {
      setFilterData({
        category: activeProjectCategory,
        subcategory: activeProjectSubcategory,
      });
      
      // If there's already a category selected, fetch its subcategories
      if (activeProjectCategory) {
        setActiveTab("subcategory");
        fetchSubcategoriesForCategory(activeProjectCategory.name);
      } else {
        setActiveTab("category");
        setLocalSubcategories([]);
      }
    }
  }, [open]); // Only depend on open, not on context values

  // Separate effect to handle category changes when drawer is open
  useEffect(() => {
    if (open && filterData.category && 
        lastSelectedCategoryRef.current !== filterData.category?.name) {
      
      lastSelectedCategoryRef.current = filterData.category?.name;
      console.log('Category changed to:', filterData.category.name);
      
      // Delay tab switch slightly to prevent flash
      setIsTabSwitchInProgress(true);
      setTimeout(() => {
        setActiveTab("subcategory");
        fetchSubcategoriesForCategory(filterData.category.name);
        setIsTabSwitchInProgress(false);
      }, 100);
    }
  }, [filterData.category, open]);

  const handleClose = () => {
    setOpen(false);
    // Reset refs
    lastSelectedCategoryRef.current = null;
    setIsTabSwitchInProgress(false);
  };

  const handleChangeTab = (_, value) => {
    // Prevent manual tab switch when category selection is in progress
    if (!isTabSwitchInProgress) {
      setActiveTab(value);
    }
  };

  const handleCheckboxChange = (type, item) => {
    console.log('Checkbox changed:', type, item);
    
    if (type === "category") {
      const isSame = filterData.category?.name === item.name;
      const newCategory = isSame ? null : item;
      
      console.log('Category selection:', {
        isSame,
        newCategory: newCategory?.name,
        currentCategory: filterData.category?.name
      });
      
      setFilterData(prev => ({
        category: newCategory,
        subcategory: null, // Clear subcategory when category changes
      }));
      
    } else {
      const isSame = filterData.subcategory?.name === item.name;
      setFilterData(prev => ({
        ...prev,
        subcategory: isSame ? null : item,
      }));
    }
  };

  const fetchSubcategoriesForCategory = async (categoryName) => {
    if (!categoryName) {
      console.log('No category name provided, clearing subcategories');
      setLocalSubcategories([]);
      return;
    }

    try {
      setLoadingSubcategories(true);
      console.log('Fetching subcategories for category:', categoryName);
      
      // Call API with category name as parentName
      const response = await getProjectSubcategoryList_API(`parentName=${encodeURIComponent(categoryName)}`);
      
      console.log('API Response:', response);
      
      if (response.status === HttpStatusCode.Ok) {
        const subcategories = response.data;
        console.log('Fetched subcategories:', subcategories);
        setLocalSubcategories(subcategories);
      } else {
        console.error('Failed to fetch subcategories:', response);
        setLocalSubcategories([]);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setLocalSubcategories([]);
    } finally {
      setLoadingSubcategories(false);
    }
  };

  const handleClearTab = (type) => {
    console.log('Clearing tab:', type);
    if (type === "category") {
      setFilterData({ category: null, subcategory: null });
      setLocalSubcategories([]);
      setActiveTab("category");
      lastSelectedCategoryRef.current = null;
    } else {
      setFilterData(prev => ({ ...prev, subcategory: null }));
    }
  };

  const handleApply = () => {
    console.log('Applying filters:', filterData);
    // Only update context when Apply is clicked
    setActiveProjectCategory(filterData.category);
    setActiveProjectSubcategory(filterData.subcategory);
    setOpen(false);
  };

  const handleReset = () => {
    console.log('Resetting all filters');
    setFilterData({ category: null, subcategory: null });
    setLocalSubcategories([]);
    setActiveTab("category");
    lastSelectedCategoryRef.current = null;
    // Clear context filters
    setActiveProjectCategory(null);
    setActiveProjectSubcategory(null);
    setOpen(false);
  };

  const getTabValues = () => {
    if (activeTab === "category") {
      console.log('Debug - Category List:', categoryList);
      return Array.isArray(categoryList) ? categoryList : [];
    }

    if (activeTab === "subcategory") {
      if (!filterData.category) {
        console.log('Debug - No category selected for subcategories');
        return [];
      }
      
      // Return the locally fetched subcategories for the selected category
      return localSubcategories;
    }

    return [];
  };

  const getFilteredSubcategories = () => {
    if (!filterData.category) return [];
    return localSubcategories;
  };

  const tabValues = getTabValues();
  const hasTabValues = tabValues.length > 0;

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      anchor="right"
      PaperProps={{
        sx: {
          width: 420,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.paper,
          backdropFilter: "blur(10px)",
          borderLeft: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          boxShadow: theme.palette.mode === 'dark'
            ? `-4px 0 20px ${alpha(theme.palette.primary.main, 0.15)}`
            : `-4px 0 20px ${alpha(theme.palette.primary.main, 0.08)}`,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          backgroundColor: alpha(theme.palette.background.default, 0.8),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <FilterAltOutlined 
            sx={{ 
              color: theme.palette.primary.main,
              fontSize: font.size.fs_4 
            }} 
          />
          <Typography 
            fontSize={font.size.fs_4} 
            fontWeight={600}
            sx={{ color: theme.palette.text.primary }}
          >
            Filter Projects
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose}
          sx={{
            color: theme.palette.text.secondary,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            }
          }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, display: "flex" }}>
        {/* Left Tabs */}
        <Box
          sx={{
            width: "40%",
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            backgroundColor: alpha(theme.palette.background.default, 0.5),
            pt: 2,
          }}
        >
          <Tabs
            orientation="vertical"
            value={activeTab}
            onChange={handleChangeTab}
            sx={{
              "& .MuiTab-root": {
                alignItems: "flex-start",
                minHeight: 56,
                px: 3,
                py: 1.5,
                textTransform: "none",
                transition: "all 0.2s ease",
                "&.Mui-selected": {
                  color: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
                "&:hover": {
                  backgroundColor: alpha(theme.palette.action.hover, 0.5),
                },
              },
              "& .MuiTabs-indicator": {
                left: 0,
                width: 4,
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            {["category", "subcategory"].map((tab) => {
              const count = filterData[tab] ? 1 : 0;
              const isDisabled = tab === "subcategory" && !filterData.category;

              return (
                <Tab
                  key={tab}
                  value={tab}
                  disabled={isDisabled || isTabSwitchInProgress}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                      <Typography 
                        fontSize={font.size.fs_5}
                        sx={{
                          color: isDisabled ? theme.palette.text.disabled : "inherit",
                          opacity: isDisabled || isTabSwitchInProgress ? 0.5 : 1,
                        }}
                      >
                        {tab === "category" ? "Category" : "Subcategory"}
                        {tab === "subcategory" && filterData.category && (
                          <Typography 
                            component="span" 
                            fontSize={font.size.fs_6}
                            sx={{ 
                              ml: 1,
                              color: theme.palette.text.secondary,
                              opacity: 0.7,
                            }}
                          >
                            ({getFilteredSubcategories().length})
                          </Typography>
                        )}
                      </Typography>

                      {count > 0 && (
                        <Chip
                          label={count}
                          size="small"
                          sx={{
                            height: 20,
                            minWidth: 20,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                          }}
                        />
                      )}
                    </Box>
                  }
                />
              );
            })}
          </Tabs>

          {/* Selected Category and Subcategory Display */}
          <Box sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
            {filterData.category && (
              <>
                <Typography 
                  fontSize={font.size.fs_6}
                  sx={{ color: theme.palette.text.primary, mb: 1 }}
                >
                  Selected Category:
                </Typography>
                <Chip
                  label={filterData.category.name}
                  size="small"
                  onDelete={() => handleCheckboxChange("category", filterData.category)}
                  deleteIcon={<Clear fontSize="small" />}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    color: theme.palette.primary.main,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.primary.main,
                      '&:hover': {
                        color: theme.palette.primary.dark,
                      }
                    },
                    mb: 1
                  }}
                />
              </>
            )}

            {filterData.subcategory && (
              <>
                <Typography 
                  fontSize={font.size.fs_6}
                  sx={{ color: theme.palette.text.primary, mb: 1, mt: filterData.category ? 2 : 0 }}
                >
                  Selected Subcategory:
                </Typography>
                <Chip
                  label={filterData.subcategory.name}
                  size="small"
                  onDelete={() => handleCheckboxChange("subcategory", filterData.subcategory)}
                  deleteIcon={<Clear fontSize="small" />}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    color: theme.palette.primary.main,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.primary.main,
                      '&:hover': {
                        color: theme.palette.primary.dark,
                      }
                    }
                  }}
                />
              </>
            )}

            {!filterData.category && !filterData.subcategory && (
              <Typography 
                fontSize={font.size.fs_6}
                sx={{ color: theme.palette.text.disabled, fontStyle: 'italic' }}
              >
                No filters selected
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right Pane */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography 
              fontSize={font.size.fs_4}
              fontWeight={600}
              sx={{ color: theme.palette.text.primary }}
            >
              {activeTab === "category" ? "Select Category" : "Select Subcategory"}
              {activeTab === "subcategory" && filterData.category && (
                <Typography 
                  component="span" 
                  fontSize={font.size.fs_6}
                  sx={{ 
                    ml: 1,
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                  }}
                >
                  for "{filterData.category.name}"
                </Typography>
              )}
            </Typography>
            <Button 
              size="small" 
              onClick={() => handleClearTab(activeTab)}
              startIcon={<Clear fontSize="small" />}
              disabled={isTabSwitchInProgress}
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  color: theme.palette.error.main,
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                },
                "&.Mui-disabled": {
                  color: theme.palette.text.disabled,
                }
              }}
            >
              Clear 
            </Button>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto" }}>
            {activeTab === "subcategory" && loadingSubcategories ? (
              <Box sx={{ 
                display: "flex", 
                flexDirection: "column", 
                alignItems: "center", 
                justifyContent: "center", 
                height: "100%",
                color: theme.palette.text.secondary,
                textAlign: "center",
                p: 3
              }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography fontSize={font.size.fs_5}>
                  Loading subcategories...
                </Typography>
              </Box>
            ) : hasTabValues ? (
              tabValues.map((item, index) => (
                <Box
                  key={index}
                  onClick={() => !isTabSwitchInProgress && handleCheckboxChange(activeTab, item)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    py: 1.5,
                    px: 1,
                    borderRadius: "8px",
                    cursor: isTabSwitchInProgress ? 'default' : 'pointer',
                    transition: "all 0.2s ease",
                    backgroundColor: filterData[activeTab]?.name === item.name
                      ? alpha(theme.palette.primary.main, 0.1)
                      : "transparent",
                    border: `1px solid ${
                      filterData[activeTab]?.name === item.name
                        ? alpha(theme.palette.primary.main, 0.3)
                        : "transparent"
                    }`,
                    "&:hover": !isTabSwitchInProgress ? {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                    } : {},
                    mb: 1,
                    opacity: isTabSwitchInProgress ? 0.7 : 1,
                  }}
                >
                  <Checkbox
                    checked={filterData[activeTab]?.name === item.name}
                    onChange={() => !isTabSwitchInProgress && handleCheckboxChange(activeTab, item)}
                    disabled={isTabSwitchInProgress}
                    sx={{
                      color: theme.palette.text.secondary,
                      '&.Mui-checked': {
                        color: theme.palette.primary.main,
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'inherit',
                      },
                      mr: 2,
                      opacity: isTabSwitchInProgress ? 0.5 : 1,
                    }}
                  />
                  <Typography
                    fontSize={font.size.fs_5}
                    sx={{
                      color: filterData[activeTab]?.name === item.name
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                      fontWeight: filterData[activeTab]?.name === item.name ? 600 : 400,
                      opacity: isTabSwitchInProgress ? 0.7 : 1,
                    }}
                  >
                    {item.name}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box sx={{ 
                display: "flex", 
                flexDirection: "column", 
                alignItems: "center", 
                justifyContent: "center", 
                height: "100%",
                color: theme.palette.text.secondary,
                textAlign: "center",
                p: 3
              }}>
                <FilterAltOutlined sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography fontSize={font.size.fs_5} sx={{ mb: 1 }}>
                  {activeTab === "subcategory" && !filterData.category
                    ? "Please select a category first"
                    : activeTab === "subcategory" && filterData.category
                    ? `No subcategories found for "${filterData.category.name}"`
                    : "No categories available"}
                </Typography>
                <Typography fontSize={font.size.fs_6} sx={{ opacity: 0.7 }}>
                  {activeTab === "subcategory" && !filterData.category
                    ? "Select a category to see available subcategories"
                    : activeTab === "subcategory" && filterData.category
                    ? "Try selecting a different category"
                    : "There are no categories to display"}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          p: 3,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          backgroundColor: alpha(theme.palette.background.default, 0.8),
        }}
      >
        <Button
          variant="outlined"
          fullWidth
          onClick={handleReset}
          disabled={isTabSwitchInProgress}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            height: 44,
            borderColor: theme.palette.divider,
            color: theme.palette.text.secondary,
            "&:hover": {
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
              backgroundColor: alpha(theme.palette.error.main, 0.1),
            },
            "&.Mui-disabled": {
              borderColor: theme.palette.divider,
              color: theme.palette.text.disabled,
            }
          }}
        >
          Reset All
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={handleApply}
          disabled={(!filterData.category && !filterData.subcategory) || isTabSwitchInProgress}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            height: 44,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
            color: theme.palette.primary.contrastText,
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            "&:hover": {
              background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.success.main, 0.9)})`,
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
            "&.Mui-disabled": {
              background: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
              boxShadow: "none",
              transform: "none",
            },
            transition: "all 0.2s ease",
          }}
        >
          Apply Filters
        </Button>
      </Box>
    </Drawer>
  );
}